import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import * as Sentry from '@sentry/react';
import { NotificationContent } from './style';
import { Button } from 'components/ui/button';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { UPDATE_BILLING_NOTIFICATIONS } from 'app/graphql';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { Loader2 } from 'lucide-react';
import { BillingNotifications } from 'app/graphql/generated/admin/graphql';
import { isEmail } from 'lib/is-email';
import { Label } from 'components/ui/label';
import { EmailTagInput } from 'components/EmailTagInput';
import { toast } from 'sonner';
import { canWrite } from 'app/access';

type Props = {
  onSubmit: (billingNotifications: BillingNotifications) => void;
};

export const NotificationSettingsDialog = ({ onSubmit }: Props) => {
  const userState = useAppSelector(selectUserInfo);
  const canEditBilling = canWrite(userState, 'billing');
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const [emails, setEmails] = useState<string[]>(
    userState?.billingNotificationSettings?.emails || [],
  );

  useEffect(() => {
    setEmails(userState?.billingNotificationSettings?.emails || []);

    return () => {
      clearTimeout(loadingTimerRef.current);
    };
  }, [userState?.billingNotificationSettings]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const [updateBillingNotifications] = useMutation(UPDATE_BILLING_NOTIFICATIONS, {
    context: { clientName: 'admin' },
  });

  const handleSubmit = async () => {
    if (!canEditBilling) {
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await updateBillingNotifications({
        variables: {
          input: {
            emails: emails.map((email) => email),
          },
        },
      });
      if (res.errors) {
        throw Error(JSON.stringify(res.errors));
      }

      const billingNotifications = res.data?.updateBillingNotifications.settings;
      if (!billingNotifications) {
        throw Error('No billing notifications returned');
      }
      loadingTimerRef.current = setTimeout(() => {
        setIsSubmitting(false);
        setOpen(false);
        onSubmit(billingNotifications);
        toast.success('Billing notifications updated successfully', {
          id: 'update-billing-notifications-success',
        });
      }, 1000);
    } catch (error) {
      Sentry.captureException('Failed to update billing notifications - ' + error);
      toast.error('Error updating billing notifications', {
        id: 'update-billing-notifications-failed',
      });
      setIsSubmitting(false);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {canEditBilling && (
        <DialogTrigger asChild disabled={!canEditBilling}>
          <Button type="button" variant="outline" size="sm" className="gap-2">
            <Bell className="h-3.5 w-3.5" />
            Notification settings
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Notification settings</DialogTitle>
          <DialogDescription>Update billing notification recipients.</DialogDescription>
        </DialogHeader>
        <NotificationContent className="space-y-2">
          <Label>Additional recipients</Label>
          <EmailTagInput
            emails={emails}
            setEmails={setEmails}
            disabled={!canEditBilling || isSubmitting}
            readOnly={!canEditBilling || isSubmitting}
            addTagsOnBlur={true}
            inputProps={{ inputMode: 'email' }}
            placeholder="Add email address"
            styleClasses={{
              input: 'w-full sm:max-w-[350px] shadow-none',
              tag: {
                body: 'bg-[var(--surface-2)] text-[var(--text)] border border-[var(--line)] hover:bg-[var(--surface-2)]',
              },
              tagList: { container: 'flex-wrap' },
            }}
            variant="primary"
            validateTag={(tag: string) => {
              return isEmail(tag);
            }}
          />
        </NotificationContent>
        <DialogFooter>
          <Button type="button" variant="secondary" disabled={isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
